import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Col, Form, Row, Typography } from "antd";
import {
  CheckCircleOutlined,
  CloseOutlined,
  CreditCardOutlined,
  DeleteOutlined,
  EditOutlined,
  LockOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { PaymentForm, CreditCard } from "react-square-web-payments-sdk";
import { openConfirmNotification, openErrorNotification } from "../utils/toast";
import CommonButton from "./CommonButton";
import CustomInput from "./inputWidgets/CustomInput";
import axios from "axios";
import useGet from "../hooks/useGet";
import usePost from "../hooks/usePost";
import { getDealerNo } from "../hooks/getUserRole";

const { Text } = Typography;

/** Square SDK renders this id on the internal Pay button; we hide it and trigger tokenize from Save/Update. */
const SQUARE_CARD_BUTTON_SELECTOR =
  "#manage-cards-square-root #rswp-card-button";

/**
 * CRA inlines env at build time. Deployed builds sometimes leave REACT_APP_API_URL
 * empty or commented — treat that as same-origin API (relative /api/...).
 */
function getApiBase() {
  const raw = process.env.REACT_APP_API_URL;
  if (raw == null) return "";
  const s = String(raw).trim();
  if (!s || s === "undefined") return "";
  return s.replace(/\/+$/, "");
}

/** Absolute base or "" for relative paths from the current origin. */
const apiBase = getApiBase();

function apiUrl(path) {
  const p = path.startsWith("/") ? path : `/${path}`;
  return apiBase ? `${apiBase}${p}` : p;
}

/** Normalize GET /api/payments/cards/{dealerNo}/ into an array of card rows. */
function normalizeCardList(response) {
  if (response == null) return [];
  const raw = response.data ?? response;
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw?.data)) return raw.data;
  if (Array.isArray(raw?.results)) return raw.results;
  if (
    raw &&
    typeof raw === "object" &&
    (raw.Last4 != null || raw.last4 != null)
  ) {
    return [raw];
  }
  return [];
}

/** Map API brand string to a stable key for logos. */
function brandKeyFromCard(card) {
  const raw = String(
    card?.Brand ?? card?.brand ?? card?.CardBrand ?? card?.card_brand ?? "",
  ).toLowerCase();
  if (raw.includes("visa")) return "visa";
  if (raw.includes("master") || raw.includes("mc")) return "mastercard";
  if (raw.includes("amex") || raw.includes("american")) return "amex";
  if (raw.includes("discover")) return "discover";
  if (raw.includes("diners")) return "diners";
  if (raw.includes("jcb")) return "jcb";
  if (raw.includes("union")) return "unionpay";
  return "generic";
}

/** Decorative row of common networks (simplified marks, not official logos). */
function CardBrandLogosRow({ className = "" }) {
  return (
    <div
      className={`flex flex-wrap items-center gap-3 ${className}`}
      aria-hidden
    >
      <span
        title="Visa"
        className="inline-flex h-7 min-w-[44px] items-center justify-center rounded bg-[#1A1F71] px-2"
      >
        <span className="font-[Poppins] text-[10px] font-bold italic tracking-wide text-white">
          VISA
        </span>
      </span>
      <span title="Mastercard" className="inline-flex h-7 w-11 items-center">
        <svg viewBox="0 0 40 24" className="h-7 w-11">
          <circle cx="15" cy="12" r="10" fill="#EB001B" />
          <circle cx="25" cy="12" r="10" fill="#F79E1B" />
          <path
            fill="#FF5F00"
            d="M20 5.5a9.9 9.9 0 0 1 0 13 9.9 9.9 0 0 1 0-13z"
          />
        </svg>
      </span>
      <span
        title="American Express"
        className="inline-flex h-7 min-w-[44px] items-center justify-center rounded bg-[#006FCF] px-1"
      >
        <span className="text-center font-[Poppins] text-[8px] font-bold leading-tight text-white">
          AMEX
        </span>
      </span>
      <span
        title="Discover"
        className="inline-flex h-7 min-w-[52px] items-center justify-center rounded bg-[#FF6000] px-1"
      >
        <span className="font-[Poppins] text-[8px] font-bold text-white">
          DISCOVER
        </span>
      </span>
    </div>
  );
}

function CardBrandIcon({ brandKey, className = "shrink-0" }) {
  const k = brandKey || "generic";
  if (k === "visa") {
    return (
      <span
        className={`inline-flex h-9 min-w-[52px] items-center justify-center rounded border border-[#d0d7e2] bg-white px-2 shadow-sm ${className}`}
        aria-hidden
      >
        <span className="font-[Poppins] text-[11px] font-bold italic tracking-wide text-[#1A1F71]">
          VISA
        </span>
      </span>
    );
  }
  if (k === "mastercard") {
    return (
      <span
        className={`inline-flex h-9 w-14 items-center ${className}`}
        aria-hidden
      >
        <svg viewBox="0 0 40 24" className="h-9 w-14">
          <circle cx="15" cy="12" r="10" fill="#EB001B" />
          <circle cx="25" cy="12" r="10" fill="#F79E1B" />
          <path
            fill="#FF5F00"
            d="M20 5.5a9.9 9.9 0 0 1 0 13 9.9 9.9 0 0 1 0-13z"
          />
        </svg>
      </span>
    );
  }
  if (k === "amex") {
    return (
      <span
        className={`inline-flex h-9 min-w-[52px] items-center justify-center rounded border border-[#d0d7e2] bg-[#006FCF] px-1 shadow-sm ${className}`}
        aria-hidden
      >
        <span className="text-center font-[Poppins] text-[8px] font-bold leading-tight text-white">
          AMEX
        </span>
      </span>
    );
  }
  if (k === "discover") {
    return (
      <span
        className={`inline-flex h-9 min-w-[56px] items-center justify-center rounded border border-[#d0d7e2] bg-[#FF6000] px-1 shadow-sm ${className}`}
        aria-hidden
      >
        <span className="font-[Poppins] text-[8px] font-bold text-white">
          DISC
        </span>
      </span>
    );
  }
  return (
    <span
      className={`inline-flex h-9 w-11 items-center justify-center rounded border border-[#d9d9d9] bg-gray-50 ${className}`}
      aria-hidden
    >
      <CreditCardOutlined className="text-xl text-gray-500" />
    </span>
  );
}

function cardLabel(card) {
  const brand =
    card?.Brand ?? card?.brand ?? card?.CardBrand ?? card?.card_brand ?? "Card";
  const last =
    card?.Last4 ?? card?.last4 ?? card?.LastFour ?? card?.last_four ?? "••••";
  const exp =
    card?.ExpDisplay ??
    card?.exp_display ??
    (card?.ExpMonth != null && card?.ExpYear != null
      ? `${String(card.ExpMonth).padStart(2, "0")}/${String(card.ExpYear).slice(-2)}`
      : card?.exp_month != null && card?.exp_year != null
        ? `${String(card.exp_month).padStart(2, "0")}/${String(card.exp_year).slice(-2)}`
        : "");
  return { brand, last, exp };
}

/**
 * Dealer payment Method: Square tokenization + `/api/payments/cards/`.
 * Saving (add or replace) always uses POST to `postUrl` — never PUT.
 * UI assumes one saved card per dealer: list shows Edit when a card exists.
 */
export const ManageCardsModal = ({ setIsModalOpen }) => {
  const [form] = Form.useForm();
  const [view, setView] = useState("list");
  const [saving, setSaving] = useState(false);
  const [deletingKey, setDeletingKey] = useState(null);
  const pendingRef = useRef(false);

  const dealerNo = getDealerNo();
  const dealerNoStr =
    dealerNo != null && dealerNo !== "" ? String(dealerNo) : "";

  const applicationId = process.env.REACT_APP_APPLICATION_ID;
  const locationId = process.env.REACT_APP_LOCATION_ID;

  const cardsGetUrl = useMemo(() => {
    if (!dealerNoStr) return null;
    return apiUrl(`/api/payments/cards/${dealerNoStr}/`);
  }, [dealerNoStr]);

  const { data, loading, error, refetch } = useGet(cardsGetUrl);
  /** POST only: same endpoint for new card and replacing an existing card. */
  const postUrl = apiUrl("/api/payments/cards/");
  const {
    data: postResult,
    loading: postLoading,
    error: postError,
    postData,
  } = usePost(postUrl);

  const cards = useMemo(() => normalizeCardList(data), [data]);

  const hasSavedCard = !loading && cards.length > 0;
  const isEditingCard = cards.length > 0;

  const prevViewRef = useRef("list");
  useEffect(() => {
    if (view !== "add") {
      prevViewRef.current = view;
      return;
    }
    const justOpenedAdd = prevViewRef.current !== "add";
    prevViewRef.current = view;
    if (!justOpenedAdd) return;

    const first = cards[0];
    if (!first) {
      form.resetFields();
      return;
    }
    form.setFieldsValue({
      CardholderName:
        first.card_holder_name ??
        first.CardHolderName ??
        first.cardHolderName ??
        "",
      Email:
        first.card_holder_email ??
        first.CardHolderEmail ??
        first.Email ??
        first.email ??
        "",
    });
  }, [view, cards, form]);

  const resetAddForm = useCallback(() => {
    form.resetFields();
    setSaving(false);
    pendingRef.current = false;
  }, [form]);

  const handleBackToList = () => {
    setView("list");
    resetAddForm();
  };

  useEffect(() => {
    if (postError) {
      setSaving(false);
      pendingRef.current = false;
      openErrorNotification(
        postError?.response?.data?.message || "Could not save card.",
      );
    } else if (!postLoading && !postError && postResult) {
      setSaving(false);
      pendingRef.current = false;
      openConfirmNotification("Success");
      resetAddForm();
      setView("list");
      refetch?.();
    }
  }, [postError, postResult, postLoading, refetch, resetAddForm]);

  const handleTokenReceived = useCallback(
    async (tokenResult) => {
      setSaving(false);
      pendingRef.current = false;

      if (tokenResult?.status !== "OK" || !tokenResult?.token) {
        const err0 = tokenResult?.errors?.[0];
        const msg =
          (typeof err0 === "object" && err0?.message) ||
          (err0 instanceof Error ? err0.message : null) ||
          (typeof err0 === "string" ? err0 : null) ||
          "Could not tokenize card. Check card details.";
        openErrorNotification(String(msg));
        return;
      }

      const cardholderName = String(
        form.getFieldValue("CardholderName") ?? "",
      ).trim();
      const email = String(form.getFieldValue("Email") ?? "").trim();
      if (!cardholderName) {
        openErrorNotification("Please enter the cardholder name.");
        return;
      }
      if (!email) {
        openErrorNotification("Please enter an email address.");
        return;
      }

      if (!dealerNoStr) {
        openErrorNotification("Dealer account not found.");
        return;
      }

      const dealerId = Number.parseInt(dealerNoStr, 10);
      if (!Number.isFinite(dealerId)) {
        openErrorNotification("Dealer account not found.");
        return;
      }

      await postData({
        dealer_id: dealerId,
        source_id: String(tokenResult.token),
        card_holder_name: cardholderName,
        card_holder_email: email,
      });
    },
    [dealerNoStr, form, postData],
  );

  const handleSaveCard = async () => {
    try {
      await form.validateFields(["CardholderName", "Email"]);
    } catch {
      return;
    }
    if (!applicationId || !locationId) {
      openErrorNotification(
        "Square is not configured (application or location ID missing).",
      );
      return;
    }
    if (!dealerNoStr) {
      openErrorNotification("Dealer account not found.");
      return;
    }
    setSaving(true);
    pendingRef.current = true;
    requestAnimationFrame(() => {
      const btn = document.querySelector(SQUARE_CARD_BUTTON_SELECTOR);
      if (btn) {
        btn.click();
      } else {
        document.querySelector("#manage-cards-square-root button")?.click();
      }
      if (pendingRef.current) {
        setTimeout(() => {
          if (pendingRef.current) {
            pendingRef.current = false;
            setSaving(false);
            openErrorNotification(
              "Card form is not ready. Wait a moment and try again.",
            );
          }
        }, 15000);
      }
    });
  };

  const handleDeleteCard = async (card) => {
    if (!dealerNoStr) return;
    const pk =
      card?.CardNo ?? card?.card_no ?? card?.id ?? card?.pk ?? dealerNoStr;
    const url = apiUrl(`/api/payments/cards/${pk}/`);
    setDeletingKey(String(pk));
    try {
      const token = JSON.parse(sessionStorage.getItem("userData"));
      const access_token = token?.access_token;
      await axios.delete(url, {
        headers: { Authorization: `Bearer ${access_token}` },
      });
      openConfirmNotification("Success");
      refetch?.();
    } catch (err) {
      openErrorNotification(
        err?.response?.data?.message || "Could not remove card.",
      );
    } finally {
      setDeletingKey(null);
    }
  };

  const shellHeader = (title) => (
    <div className="bg-gray-200 py-[8px] px-[3px] rounded-sm flex items-center mb-2 p-2">
      <CreditCardOutlined className="ml-2 text-[16px]" />
      <h2 className="text-[14px] font-[Poppins] text-black ml-2">{title}</h2>
    </div>
  );

  if (!dealerNoStr) {
    const noDealerButtons = [
      {
        label: "Cancel",
        icon: <CloseOutlined />,
        onClick: () => setIsModalOpen(false),
      },
    ];
    return (
      <div className="bg-white rounded-[4px] overflow-hidden">
        {shellHeader("Payment Method")}
        <div className="mt-4 px-2">
          <Text type="danger">
            No dealer account is associated with this login. Payment method is
            only available for dealer accounts.
          </Text>
        </div>
        <div className="flex justify-center items-center pt-3">
          <CommonButton buttonsData={noDealerButtons} />
        </div>
      </div>
    );
  }

  if (!applicationId || !locationId) {
    const envButtons = [
      {
        label: "Cancel",
        icon: <CloseOutlined />,
        onClick: () => setIsModalOpen(false),
      },
    ];
    return (
      <div className="bg-white rounded-[4px] overflow-hidden">
        {shellHeader("Payment Method")}
        <div className="mt-4 px-2">
          <Text type="danger">
            Square application ID or location ID is missing from environment
            variables.
          </Text>
        </div>
        <div className="flex justify-center items-center pt-3">
          <CommonButton buttonsData={envButtons} />
        </div>
      </div>
    );
  }

  if (view === "list") {
    const listButtons = [
      {
        label: hasSavedCard ? "Edit card" : "Add card",
        icon: hasSavedCard ? <EditOutlined /> : <PlusOutlined />,
        onClick: () => setView("add"),
      },
      {
        label: "Cancel",
        icon: <CloseOutlined />,
        onClick: () => setIsModalOpen(false),
      },
    ];

    return (
      <div className="bg-white rounded-[4px] overflow-hidden">
        {shellHeader("Payment Method")}
        <div className="mt-4 px-2">
          <Row gutter={[16, 12]}>
            {error ? (
              <Col span={24}>
                <Text type="danger" className="pl-0.5">
                  {error?.response?.data?.message ||
                    "Could not load payment method."}
                </Text>
              </Col>
            ) : loading ? (
              <Col span={24}>
                <p className="text-sm text-black font-[Poppins] pl-0.5">
                  Loading…
                </p>
              </Col>
            ) : cards.length === 0 ? (
              <Col span={24}>
                <p className="text-sm text-black font-[Poppins] pl-0.5">
                  No payment method added yet.
                </p>
              </Col>
            ) : (
              cards.map((card, idx) => {
                const { brand, last, exp } = cardLabel(card);
                const key = card?.CardNo ?? card?.card_no ?? card?.id ?? idx;
                return (
                  <Col span={24} key={String(key)}>
                    <div className="border border-[#d9d9d9] rounded p-3 flex flex-wrap items-center justify-between gap-2">
                      <div className="flex items-start gap-3 min-w-0 flex-1">
                        <CardBrandIcon brandKey={brandKeyFromCard(card)} />
                        <div className="min-w-0">
                          <p className="text-sm font-medium font-[Poppins] m-0">
                            {brand} •••• {last}
                          </p>
                          {exp ? (
                            <p className="text-xs text-gray-600 m-0 mt-1">
                              Exp: {exp}
                            </p>
                          ) : null}
                          {card?.Email || card?.email ? (
                            <p className="text-xs text-gray-600 m-0 mt-1">
                              {card?.Email ?? card?.email}
                            </p>
                          ) : null}
                        </div>
                      </div>
                      <CommonButton
                        buttonsData={[
                          {
                            label: "Delete",
                            icon: <DeleteOutlined />,
                            onClick: () => handleDeleteCard(card),
                            loading: deletingKey === String(key),
                            disabled: deletingKey != null,
                          },
                        ]}
                      />
                    </div>
                  </Col>
                );
              })
            )}
          </Row>
        </div>
        <div className="flex justify-center items-center pt-3">
          <CommonButton buttonsData={listButtons} />
        </div>
      </div>
    );
  }

  const addButtons = [
    {
      label: "Save card",
      icon: <CheckCircleOutlined />,
      onClick: handleSaveCard,
      loading: saving || postLoading,
    },
    {
      label: "Cancel",
      icon: <CloseOutlined />,
      onClick: handleBackToList,
    },
  ];

  return (
    <div className="bg-white rounded-[4px] overflow-hidden">
      {shellHeader(isEditingCard ? "Edit card" : "Add card")}
      <Form
        validateTrigger="onChange"
        form={form}
        layout="vertical"
        autoComplete="off"
        scrollToFirstError
      >
        <div className="mt-4 px-2">
          <Row gutter={[16, 8]}>
            <Col span={24}>
              <CustomInput
                label="Cardholder name"
                name="CardholderName"
                placeholder="Name on card"
                autoComplete="cc-name"
                required
              />
            </Col>
            <Col span={24}>
              <CustomInput
                label="Email address"
                name="Email"
                placeholder="Email"
                autoComplete="email"
                isEmail
                required
              />
            </Col>
            <Col span={24}>
              <label className="inline-flex w-full text-sm text-black form-label pl-0.5 py-0.5 mb-1">
                Card information
                <span className="text-red-500 ml-0.5">*</span>
              </label>
              {/* <p className="text-xs text-gray-500 mb-2 pl-0.5">Accepted cards</p>
              <CardBrandLogosRow className="mb-3 pl-0.5" /> */}
              <div
                id="manage-cards-square-root"
                className="relative rounded border border-[#d9d9d9] px-2 py-2 bg-white min-h-[48px] overflow-hidden"
              >
                <PaymentForm
                  applicationId={applicationId}
                  locationId={locationId}
                  cardTokenizeResponseReceived={handleTokenReceived}
                >
                  <CreditCard
                    render={(Button) => (
                      <Button
                        aria-hidden
                        css={{
                          position: "absolute",
                          left: "-9999px",
                          top: 0,
                          width: "1px",
                          height: "1px",
                          padding: 0,
                          margin: 0,
                          overflow: "hidden",
                          clipPath: "inset(50%)",
                          border: "none",
                          opacity: 0,
                        }}
                      >
                        {""}
                      </Button>
                    )}
                  />
                </PaymentForm>
              </div>
            </Col>
            <Col span={24}>
              <div className="flex items-center gap-2 text-gray-500 text-sm pl-0.5">
                <LockOutlined />
                <span>Your card is securely processed.</span>
              </div>
            </Col>
          </Row>
        </div>
        <div className="flex justify-center items-center pt-3">
          <CommonButton buttonsData={addButtons} />
        </div>
      </Form>
    </div>
  );
};
